import { useRef } from 'react';

import {
   AiAssistantBudgetSummary,
   AiAssistantBudgetSummaryQuery,
   AiAssistantConfiguration,
   AiAssistantConfigurationQuery,
   HealthcareProviderAiAssistantTokenPackage,
   HealthcareProviderAiAssistantTokenPackageQuery,
   HealthcareProviderAiAssistantTokenPackageStatus,
} from '@dreryk/egabinet-ai-assistant-frontend-api';

import { useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { commonTranslationKeys } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

type AiAssistantSettingsPanelLoaderRenderProps = Readonly<{
   budgetSummary: AiAssistantBudgetSummary;
   effectiveEnabled: boolean;
   enabledByProvider: boolean;
   tokenPackages: HealthcareProviderAiAssistantTokenPackage[];
}>;

type AiAssistantSettingsPanelLoaderProps = Readonly<{
   children: (renderProps: AiAssistantSettingsPanelLoaderRenderProps) => JSX.Element;
}>;

export const AiAssistantSettingsPanelLoader = (props: AiAssistantSettingsPanelLoaderProps): JSX.Element => {
   const aiAssistantConfigurationRefreshedRef = useRef(false);
   const {
      data: aiAssistantConfiguration,
      loading: aiAssistantConfigurationLoading,
      fetching: aiAssistantConfigurationFetching,
      error: aiAssistantConfigurationError,
   } = useCqrsGet<AiAssistantConfiguration>(() => new AiAssistantConfigurationQuery(), {
      refetchOnMount: 'always',
   });

   if (!aiAssistantConfigurationFetching) {
      aiAssistantConfigurationRefreshedRef.current = true;
   }

   const {
      data: tokenPackages,
      loading: tokenPackagesLoading,
      error: tokenPackagesError,
   } = useCqrsSearch<HealthcareProviderAiAssistantTokenPackage>(() => {
      const query = new HealthcareProviderAiAssistantTokenPackageQuery();
      query.status = Slot.of<HealthcareProviderAiAssistantTokenPackageStatus[]>(['ACTIVE', 'UTILIZED']);
      return query;
   }, paginations.all());

   const {
      data: budgetSummary,
      loading: budgetSummaryLoading,
      error: budgetSummaryError,
   } = useCqrsGet<AiAssistantBudgetSummary>(() => new AiAssistantBudgetSummaryQuery());

   const aiAssistantConfigurationRefreshing =
      aiAssistantConfigurationLoading || !aiAssistantConfigurationRefreshedRef.current;

   if (aiAssistantConfigurationRefreshing || tokenPackagesLoading || budgetSummaryLoading) {
      return <LoaderSpinner />;
   }

   if (aiAssistantConfigurationError || tokenPackagesError || budgetSummaryError) {
      return (
         <Message
            testId={'aiAssistantConfigurationFetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return props.children({
      budgetSummary: budgetSummary,
      effectiveEnabled: Boolean(aiAssistantConfiguration.effectiveEnabled),
      enabledByProvider: Boolean(aiAssistantConfiguration.enabledByProvider),
      tokenPackages: tokenPackages ?? [],
   });
};
